import { useEffect } from "react";
import { create } from "zustand";

const STORAGE_KEY = "clipra-admin-theme";

const readInitial = () => {
  if (typeof window === "undefined") return "dark";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // localStorage may be disabled — fall through
  }
  // Honour system preference on first visit; default to dark to stay
  // consistent with the cinematic look the rest of the product uses.
  if (window.matchMedia?.("(prefers-color-scheme: light)").matches) return "light";
  return "dark";
};

const applyToHtml = (theme) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  root.style.colorScheme = theme;
};

export const useTheme = create((set, get) => ({
  theme: "dark", // overwritten by initFromStorage on app boot
  initFromStorage: () => {
    const t = readInitial();
    applyToHtml(t);
    set({ theme: t });
  },
  toggle: () => {
    const next = get().theme === "dark" ? "light" : "dark";
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore quota / private mode
    }
    applyToHtml(next);
    set({ theme: next });
  },
  setTheme: (t) => {
    if (t !== "dark" && t !== "light") return;
    try {
      window.localStorage.setItem(STORAGE_KEY, t);
    } catch {
      // ignore
    }
    applyToHtml(t);
    set({ theme: t });
  },
}));

/**
 * Hook used by AdminThemeBoundary on mount: applies the persisted theme
 * to <html> exactly once. Safe to call repeatedly — it just re-applies.
 */
export const useThemeInit = () => {
  const initFromStorage = useTheme((s) => s.initFromStorage);
  useEffect(() => {
    initFromStorage();
  }, [initFromStorage]);
};
